import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container, 
  CircularProgress,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { neon } from '@neondatabase/serverless';

// Get the database URL
const getDatabaseUrl = () => {
  return 'postgres://neondb_owner:npg_Ub9EiNceZ3ro@ep-divine-sun-a18caqng-pooler.ap-southeast-1.aws.neon.tech/neondb';
};

const FeedbackResults = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [databaseUrl] = useState(() => getDatabaseUrl());
  const [memberId] = useState(() => localStorage.getItem('memberId'));
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Load feedbacks when component mounts
  useEffect(() => {
    fetchFeedbacks();
  }, []);

  // Function to fetch feedbacks from the database
  const fetchFeedbacks = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!databaseUrl) {
        throw new Error('No database URL available');
      }
      
      const sql = neon(databaseUrl, { ssl: false });
      
      // First check if the table exists
      const tableExists = await sql`
        SELECT EXISTS (
          SELECT FROM information_schema.tables 
          WHERE table_name = 'feedback'
        );
      `;
      
      if (!tableExists[0].exists) {
        setFeedbacks([]);
        setIsLoading(false);
        return;
      }
      
      // Fetch feedbacks filtered by member_id if available
      const results = await sql`
        SELECT 
          f.*,
          m.username as member_name
        FROM feedback f
        LEFT JOIN members m ON f.member_id = m.id
        WHERE ${memberId ? sql`f.member_id = ${parseInt(memberId)}` : sql`1=1`}
        ORDER BY f.created_at DESC
      `;
      
      setFeedbacks(results);
      
    } catch (err) {
      console.error('Error fetching feedbacks:', err);
      setError(`Failed to fetch feedbacks: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    // Create a date object that's explicitly in the Asia/Taipei timezone
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Taipei'
    });
  };

  // Handle opening the image dialog
  const handleOpenImageDialog = (imageData) => {
    setSelectedImage(imageData);
    setImageDialogOpen(true);
  };

  // Handle closing the image dialog
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  // Parse and display system info
  const renderSystemInfo = (systemInfoStr) => {
    if (!systemInfoStr) return null;
    
    try {
      const systemInfo = typeof systemInfoStr === 'string' ? JSON.parse(systemInfoStr) : systemInfoStr;
      
      return (
        <Grid container spacing={2}>
          {Object.entries(systemInfo).map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Typography variant="body2">
                <strong>{key}:</strong> {value}
              </Typography>
            </Grid>
          ))}
        </Grid>
      );
    } catch (err) {
      console.error('Error parsing system info:', err);
      return <Typography color="error">Error displaying system information</Typography>;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            用戶回饋列表
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchFeedbacks}
            disabled={isLoading}
          >
            刷新資料
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : feedbacks.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1">
              尚無回饋資料
            </Typography>
          </Paper>
        ) : (
          <>
            {feedbacks.map((feedback) => (
              <Paper key={feedback.id} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    回饋時間: {formatTimestamp(feedback.created_at)}
                  </Typography>
                  <Typography variant="body1">
                    {feedback.member_name ? `提交者: ${feedback.member_name}` : '提交者: (訪客)'}
                  </Typography>
                </Box>
                
                {feedback.conversation_id && (
                  <Box sx={{ mb: 2 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => window.open(`/history?view=${feedback.conversation_id}`, '_blank')}
                    >
                      查看相關對話 #{feedback.conversation_id}
                    </Button>
                  </Box>
                )}
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    回饋內容:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="body1">
                      {feedback.feedback_text || '(未提供文字回饋)'}
                    </Typography>
                  </Paper>
                </Box>
                
                {feedback.screenshot_path && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      螢幕截圖:
                    </Typography>
                    <Card sx={{ maxWidth: 345, cursor: 'pointer' }} onClick={() => handleOpenImageDialog(feedback.screenshot_path)}>
                      <CardMedia
                        component="img"
                        height="194"
                        image={`data:image/png;base64,${feedback.screenshot_path}`}
                        alt="Screenshot"
                        sx={{ objectFit: 'contain' }}
                      />
                      <CardContent sx={{ py: 1 }}>
                        <Button
                          startIcon={<ZoomInIcon />}
                          size="small"
                          fullWidth
                        >
                          放大查看
                        </Button>
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {feedback.system_info && (
                  <Accordion sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">系統資訊</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {renderSystemInfo(feedback.system_info)}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Paper>
            ))}
          </>
        )}
      </Box>
      
      {/* Full-size image dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          螢幕截圖
          <IconButton
            aria-label="close"
            onClick={handleCloseImageDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <img
                src={`data:image/png;base64,${selectedImage}`}
                alt="Full-size screenshot"
                style={{ maxWidth: '100%', maxHeight: '80vh' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FeedbackResults; 